import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { updateDevoteeInfo } from '../redux/slices/bookingSlice';
import StepperButtons from '../components/StepperButtons';
import RegistrationStepperHeader from '../components/RegistrationStepperHeader';
import Login from '../components/Login';
import eventService from '../services/eventService';
import Swal from 'sweetalert2';

const DevoteeDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { eventDetails, selectedTimeOption, devoteeInfo, totalPrice } = useSelector((state) => state.booking);
  const { selectedEvent, selectedCenter } = useSelector((state) => state.event);
  const [showLogin, setShowLogin] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    dateOfBirth: '',
    gender: '',
    relationship: 'Self',
    gothram: '',
    nastram: '',
    numberOfFamilyMembers: 0,
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [existingStudentId, setExistingStudentId] = useState(null);
  const [originalFormData, setOriginalFormData] = useState(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Check if time slots exist - if they do and none selected, redirect
    const timeOptions = eventDetails?.timeOptions || [];
    if (timeOptions.length > 0 && !selectedTimeOption) {
      navigate('/time-slot');
      return;
    }

    if (!isAuthenticated) {
      setShowLogin(true);
      return;
    }
  }, [eventDetails, selectedTimeOption, isAuthenticated, navigate]);

  // Separate useEffect to populate form data when user becomes available
  useEffect(() => {
    if (isAuthenticated && user && !hasFetchedRef.current) {
      console.log('DevoteeDetails - user object:', user);
      console.log('DevoteeDetails - user.userData:', user.userData);
      
      setShowLogin(false);
      hasFetchedRef.current = true;
      
      // Access userData nested object
      const userData = user.userData || user;
      const userId = user?.userId || user?.user_Id || user?.data?.userId || user?.data?.user_Id || user?.userData?.user_Id;
      
      // Check if this is a guest user - pre-fill from guest login data
      const isGuestUser = userData?.isGuest || userData?.is_Guest === 'Y';
      
      if (isGuestUser) {
        console.log('DevoteeDetails - Guest user detected, pre-filling from guest data');
        console.log('DevoteeDetails - devoteeInfo from Redux:', devoteeInfo);
        
        // Parse fullName into firstName and lastName for guest users
        let guestFirstName = '';
        let guestLastName = '';
        
        if (userData.fullName) {
          const nameParts = userData.fullName.trim().split(' ');
          guestFirstName = nameParts[0] || '';
          guestLastName = nameParts.slice(1).join(' ') || '';
        }
        
        // Use direct guest data fields if available (from guest login form)
        // Prioritize devoteeInfo from Redux (saved during guest login) over userData
        const firstName = devoteeInfo.firstName || userData.user_First_Name || userData.firstName || guestFirstName || '';
        const lastName = devoteeInfo.lastName || userData.user_Last_Name || userData.lastName || guestLastName || '';
        const email = devoteeInfo.email || userData.user_Email || userData.email || user.email || '';
        const phone = devoteeInfo.phone || userData.user_Phone || userData.user_Cell_Phone_No || userData.phone || '';
        
        const initialData = {
          firstName: firstName,
          lastName: lastName,
          email: email,
          phone: phone,
          dateOfBirth: '',
          gender: '',
          relationship: 'Self',
          gothram: '',
          nastram: '',
          numberOfFamilyMembers: 0,
        };
        
        console.log('DevoteeDetails - Guest user initial data:', initialData);
        setFormData(initialData);
        setOriginalFormData(initialData);
        return; // Skip the regular user data fetch for guest users
      }
      
      // Fetch existing student data for this user (devotee as student)
      const fetchDevoteeStudentData = async () => {
        try {
          if (userId) {
            const studentsData = await eventService.getStudents(userId);
            console.log('DevoteeDetails - Students data received:', studentsData);
            const students = studentsData?.students || studentsData || [];
            
            // Find student with relationship 'Self' (the devotee themselves)
            const devoteeStudent = students.find(s => 
              (s.relationship || s.student_Relationship || s.Student_Relationship_To_User || '').toLowerCase() === 'self'
            );
            
            if (devoteeStudent) {
              console.log('DevoteeDetails - Found existing devotee student record:', devoteeStudent);
              setExistingStudentId(devoteeStudent.studentId || devoteeStudent.student_Id || devoteeStudent.Student_Id);
              
              // Pre-populate form with existing student data
              const rawDOB = devoteeStudent.dateOfBirth || devoteeStudent.student_Date_Of_Birth || devoteeStudent.Student_Date_Of_Birth || userData.user_Date_Of_Birth || '';
              const rawGender = devoteeStudent.gender || devoteeStudent.student_Gender || devoteeStudent.Student_Gender || '';
              
              const initialData = {
                firstName: devoteeStudent.firstName || devoteeStudent.student_First_Name || devoteeStudent.Student_First_Name || userData.user_First_Name || '',
                lastName: devoteeStudent.lastName || devoteeStudent.student_Last_Name || devoteeStudent.Student_Last_Name || userData.user_Last_Name || '',
                email: userData.user_Email || userData.User_Email || user.User_Email || user.user_Email || user.email || '',
                phone: userData.user_Phone || userData.user_Cell_Phone_No || userData.User_Phone || userData.User_Cell_Phone_No || '',
                dateOfBirth: formatDateForInput(rawDOB),
                gender: mapGenderFromAPI(rawGender),
                relationship: 'Self',
                gothram: devoteeStudent.gotram || devoteeStudent.student_Gothram || devoteeStudent.Student_Gothram || userData.user_Gothram || '',
                nastram: devoteeStudent.nastram || devoteeStudent.student_Nakshatram || devoteeStudent.Student_Nakshatram || '',
              };
              setFormData(initialData);
              setOriginalFormData(initialData);
            } else {
              // No existing student record, use user data
              const rawDOB = devoteeInfo.dateOfBirth || userData.user_Date_Of_Birth || userData.Date_Of_Birth || '';
              const rawGender = devoteeInfo.gender || '';
              
              const initialData = {
                firstName: devoteeInfo.firstName || userData.user_First_Name || userData.User_First_Name || '',
                lastName: devoteeInfo.lastName || userData.user_Last_Name || userData.User_Last_Name || '',
                email: devoteeInfo.email || userData.user_Email || userData.User_Email || user.email || '',
                phone: devoteeInfo.phone || userData.user_Phone || userData.user_Cell_Phone_No || userData.User_Phone || userData.User_Cell_Phone_No || '',
                dateOfBirth: formatDateForInput(rawDOB),
                gender: mapGenderFromAPI(rawGender),
                relationship: 'Self',
                gothram: devoteeInfo.gothram || userData.user_Gothram || userData.Gothram || '',
                nastram: devoteeInfo.nastram || '',
              };
              setFormData(initialData);
              setOriginalFormData(initialData);
            }
          }
        } catch (error) {
          console.error('DevoteeDetails - Error fetching student data:', error);
          // Fall back to user data on error
          const rawDOB = devoteeInfo.dateOfBirth || userData.user_Date_Of_Birth || '';
          const rawGender = devoteeInfo.gender || '';
          
          const initialData = {
            firstName: devoteeInfo.firstName || userData.user_First_Name || '',
            lastName: devoteeInfo.lastName || userData.user_Last_Name || '',
            email: devoteeInfo.email || userData.user_Email || user.email || '',
            phone: devoteeInfo.phone || userData.user_Phone || userData.user_Cell_Phone_No || '',
            dateOfBirth: formatDateForInput(rawDOB),
            gender: mapGenderFromAPI(rawGender),
            relationship: 'Self',
            gothram: devoteeInfo.gothram || userData.user_Gothram || '',
            nastram: devoteeInfo.nastram || '',
          };
          setFormData(initialData);
          setOriginalFormData(initialData);
        }
      };
      
      fetchDevoteeStudentData();
    }
  }, [isAuthenticated, user, devoteeInfo]);

  // Helper function to format date from API (datetime string to YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Helper function to map gender from API (M/F) to full values (Male/Female)
  const mapGenderFromAPI = (gender) => {
    if (!gender) return '';
    const genderUpper = gender.toUpperCase();
    if (genderUpper === 'M' || genderUpper === 'MALE') return 'Male';
    if (genderUpper === 'F' || genderUpper === 'FEMALE') return 'Female';
    return gender;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const showToast = (message, type = 'success') => {
    const toast = document.createElement('div');
    toast.className = `fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg text-white font-semibold animate-slideInRight ${
      type === 'success' ? 'bg-green-500' : 'bg-red-500'
    }`;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.classList.add('opacity-0', 'transition-opacity', 'duration-500');
      setTimeout(() => document.body.removeChild(toast), 500);
    }, 3000);
  };

  const handleNext = async () => {
    const errors = {};

    console.log('=== VALIDATION DEBUG ===');
    console.log('formData.dateOfBirth:', formData.dateOfBirth, 'Type:', typeof formData.dateOfBirth, 'Length:', formData.dateOfBirth?.length);
    console.log('formData.gender:', formData.gender, 'Type:', typeof formData.gender, 'Length:', formData.gender?.length);

    // Required fields validation - check for empty or whitespace-only strings
    if (!formData.firstName || formData.firstName.trim() === '') {
      errors.firstName = 'First name is required';
    }
    if (!formData.lastName || formData.lastName.trim() === '') {
      errors.lastName = 'Last name is required';
    }
    if (!formData.email || formData.email.trim() === '') {
      errors.email = 'Email is required';
    }
    // DOB, Gender, and Relationship are now optional (hidden fields)
    // Gotram and Nakshatram are now optional fields

    console.log('Validation errors:', errors);

    // First Name validation
    if (formData.firstName && formData.firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    }
    if (formData.firstName && formData.firstName.length > 100) {
      errors.firstName = 'First name cannot exceed 100 characters';
    }

    // Last Name validation
    if (formData.lastName && formData.lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
    }
    if (formData.lastName && formData.lastName.length > 100) {
      errors.lastName = 'Last name cannot exceed 100 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (formData.email && formData.email.length > 100) {
      errors.email = 'Email cannot exceed 100 characters';
    }

    // Date of Birth validation (optional field, only validate if provided)
    // Skip validation for default/placeholder value of 1900-01-01 (database default)
    if (formData.dateOfBirth && formData.dateOfBirth.trim() !== '' && formData.dateOfBirth !== '1900-01-01') {
      const dob = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      const dayDiff = today.getDate() - dob.getDate();
      const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

      if (dob > today) {
        errors.dateOfBirth = 'Date of birth cannot be in the future';
      } else if (actualAge > 120) {
        errors.dateOfBirth = 'Please enter a valid date of birth';
      }
    }

    // Gothram validation (optional field)
    if (formData.gothram && formData.gothram.trim().length > 0 && formData.gothram.trim().length < 2) {
      errors.gothram = 'Gothram must be at least 2 characters';
    }
    if (formData.gothram && formData.gothram.length > 100) {
      errors.gothram = 'Gothram cannot exceed 100 characters';
    }

    // Nakshatram validation (optional field)
    if (formData.nastram && formData.nastram.trim().length > 0 && formData.nastram.trim().length < 2) {
      errors.nastram = 'Nakshatram must be at least 2 characters';
    }
    if (formData.nastram && formData.nastram.length > 100) {
      errors.nastram = 'Nakshatram cannot exceed 100 characters';
    }

    // If there are errors, set them and return
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Clear any existing errors
    setValidationErrors({});

    // Save devotee info to Redux
    dispatch(updateDevoteeInfo(formData));
    
    console.log('DevoteeDetails - Devotee information saved to Redux');
    
    // Create or update Student record for devotee (with relationship='Self')
    try {
      const userId = user?.userId || user?.user_Id || user?.data?.userId || user?.data?.user_Id || user?.userData?.user_Id;
      
      if (!userId) {
        console.warn('DevoteeDetails - No userId found, skipping student record creation');
        navigate('/event-payment');
        return;
      }

      // Check if data has changed (only update if changed)
      const hasDataChanged = !originalFormData || 
        formData.firstName !== originalFormData.firstName ||
        formData.lastName !== originalFormData.lastName ||
        formData.email !== originalFormData.email ||
        formData.dateOfBirth !== originalFormData.dateOfBirth ||
        formData.gender !== originalFormData.gender ||
        formData.gothram !== originalFormData.gothram ||
        formData.nastram !== originalFormData.nastram;

      if (!hasDataChanged && existingStudentId) {
        console.log('DevoteeDetails - No changes detected, skipping student update');
        navigate('/event-payment');
        return;
      }

      const studentData = {
        userId: userId,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        dateOfBirth: formData.dateOfBirth || null,
        gender: formData.gender ? (formData.gender === 'Male' ? 'M' : formData.gender === 'Female' ? 'F' : formData.gender) : null,
        relationship: 'Self',
        gotram: formData.gothram?.trim() || '',
        nastram: formData.nastram?.trim() || '',
      };

      if (existingStudentId) {
        // Update existing student record
        console.log('DevoteeDetails - Updating existing devotee student record:', existingStudentId);
        studentData.studentId = existingStudentId;
        await eventService.updateStudent(studentData);
        console.log('DevoteeDetails - Devotee student record saved successfully');
      } else {
        // Create new student record
        console.log('DevoteeDetails - Creating new devotee student record');
        const result = await eventService.addStudent(studentData);
        console.log('DevoteeDetails - Devotee student record created successfully:', result);
        if (result?.studentId || result?.student_Id) {
          setExistingStudentId(result.studentId || result.student_Id);
        }
      }

      // Update original form data to track changes
      setOriginalFormData(formData);
      
    } catch (error) {
      console.error('DevoteeDetails - Error saving devotee student record:', error);
      // Don't block navigation on error, user can continue
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'warning',
        title: 'Note: Student record not saved',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      });
    }
    
    // Navigate to next step (directly to payment)
    navigate('/event-payment');
  };

  if (showLogin && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-6">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">Login Required</h1>
              <p className="text-gray-600">Please login to continue with your booking</p>
            </div>
            <Login 
              onLoginSuccess={() => {
                setShowLogin(false);
                // Stay on current page, component will re-render with authenticated state
              }}
              onCancel={() => {
                setShowLogin(false);
                navigate('/time-slot');
              }}
              redirectPath="/devotee"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-indigo-50/70 via-white to-rose-50/70 pb-24">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-purple-200/35 blur-3xl"></div>
        <div className="absolute top-10 -right-24 h-72 w-72 rounded-full bg-blue-200/35 blur-3xl"></div>
        <div className="absolute bottom-0 left-1/3 h-80 w-80 rounded-full bg-rose-200/25 blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="overflow-hidden rounded-[28px] bg-white/75 backdrop-blur-xl shadow-[0_26px_70px_-45px_rgba(15,23,42,0.55)] border border-white/60 mb-4">
            <RegistrationStepperHeader
              title={selectedEvent?.event_Name || 'Event Registration'}
              subtitle={`${selectedEvent?.event_Date ? new Date(selectedEvent.event_Date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}${selectedEvent?.event_Time ? ` • ${selectedEvent.event_Time}` : ''}${selectedCenter?.center_Name ? ` • ${selectedCenter.center_Name}` : ''}`}
              backPath={(() => {
                const timeOptions = eventDetails?.timeOptions || [];
                const optionalServices = eventDetails?.optionalServices || [];
                if (timeOptions.length > 0) return '/time-slot';
                if (optionalServices.length > 0) return '/addon';
                return '/service';
              })()}
              backLabel="Back"
              eventDetails={eventDetails}
            />

            <div className="p-6 md:p-8">
              <div className="rounded-2xl bg-white/90 border border-gray-200 shadow-sm p-6">
                <div className="text-sm font-semibold text-gray-900 mb-1">Devotee Details</div>
                <div className="text-xs text-gray-600 mb-4">Please enter devotee details.</div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      maxLength={100}
                      className={`w-full px-4 py-2.5 border rounded-xl bg-white focus:ring-2 ${
                        validationErrors.firstName
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-200 focus:ring-indigo-500 focus:border-transparent'
                      }`}
                      required
                    />
                    {validationErrors.firstName && (
                      <p className="mt-1 text-xs text-red-500">{validationErrors.firstName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      maxLength={100}
                      className={`w-full px-4 py-2.5 border rounded-xl bg-white focus:ring-2 ${
                        validationErrors.lastName
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-200 focus:ring-indigo-500 focus:border-transparent'
                      }`}
                      required
                    />
                    {validationErrors.lastName && (
                      <p className="mt-1 text-xs text-red-500">{validationErrors.lastName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      maxLength={100}
                      className={`w-full px-4 py-2.5 border rounded-xl bg-white focus:ring-2 ${
                        validationErrors.email
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-200 focus:ring-indigo-500 focus:border-transparent'
                      }`}
                      required
                    />
                    {validationErrors.email && (
                      <p className="mt-1 text-xs text-red-500">{validationErrors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(123) 456-7890"
                      maxLength={20}
                      className={`w-full px-4 py-2.5 border rounded-xl bg-white focus:ring-2 ${
                        validationErrors.phone
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-200 focus:ring-indigo-500 focus:border-transparent'
                      }`}
                    />
                    {validationErrors.phone && (
                      <p className="mt-1 text-xs text-red-500">{validationErrors.phone}</p>
                    )}
                  </div>

                  {/* Hidden fields - DOB, Gender, Relationship kept in state but not displayed */}

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Gothram
                    </label>
                    <input
                      type="text"
                      name="gothram"
                      value={formData.gothram}
                      onChange={handleChange}
                      maxLength={100}
                      className={`w-full px-4 py-2.5 border rounded-xl bg-white focus:ring-2 ${
                        validationErrors.gothram
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-200 focus:ring-indigo-500 focus:border-transparent'
                      }`}
                    />
                    {validationErrors.gothram && (
                      <p className="mt-1 text-xs text-red-500">{validationErrors.gothram}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Nakshatram
                    </label>
                    <input
                      type="text"
                      name="nastram"
                      value={formData.nastram}
                      onChange={handleChange}
                      maxLength={100}
                      className={`w-full px-4 py-2.5 border rounded-xl bg-white focus:ring-2 ${
                        validationErrors.nastram
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-200 focus:ring-indigo-500 focus:border-transparent'
                      }`}
                    />
                    {validationErrors.nastram && (
                      <p className="mt-1 text-xs text-red-500">{validationErrors.nastram}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Number of Family Members
                    </label>
                    <input
                      type="number"
                      name="numberOfFamilyMembers"
                      value={formData.numberOfFamilyMembers}
                      onChange={handleChange}
                      min="0"
                      max="10"
                      className="w-full px-4 py-2.5 border rounded-xl bg-white focus:ring-2 border-gray-200 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <StepperButtons
        backPath={(() => {
          const timeOptions = eventDetails?.timeOptions || [];
          const optionalServices = eventDetails?.optionalServices || [];
          if (timeOptions.length > 0) return '/time-slot';
          if (optionalServices.length > 0) return '/addon';
          return '/service';
        })()}
        onNext={handleNext}
        nextLabel="Continue to Payment"
        containerClassName="px-4 py-3"
        wrapperClassName="bg-white/90 backdrop-blur-xl"
        centerContent={
          <div className="text-right">
            <div className="text-[11px] text-gray-500">Total:</div>
            <div className="text-sm font-semibold text-gray-900">${(totalPrice || 0).toFixed(2)}</div>
          </div>
        }
      />
    </div>
  );
};

export default DevoteeDetails;
